import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authFetch, authUpload } from '@/api/client'
import { ApiError, type ProblemDetail } from '@/lib/errors'
import type { Job } from '@/api/endpoints/jobs'

/**
 * Info the backend's ``probe_video`` reports after saving the upload
 * plus a ``temp_path`` we can later pass to ``/cut-video``.
 * ``download_url`` is derived client-side from ``temp_path`` — the
 * static ``/output`` mount serves the file so an HTML5 ``<video>`` can
 * play it while the user marks clips.
 */
export interface UploadedForCut {
  filename: string
  temp_path: string
  duration: number
  width: number | undefined
  height: number | undefined
  file_size: number | undefined
  /** Path under the ``/output`` static mount. Pass through
   * ``apiUrl()`` (or ``resolveDownloadUrl``) before setting on
   * ``<video src>`` so the API origin gets prefixed in production. */
  video_url: string
  /** Lightweight, densely-keyframed copy for the editor's player. Null
   * until the prepare job finishes (and on files uploaded before proxies
   * existed) — callers fall back to ``video_url``. */
  proxy_url: string | null
  /**
   * Set when the upload isn't something a browser can draw (HEVC from a
   * phone, 10-bit H.264 from a camera) and the backend is re-encoding it
   * to H.264. The file at ``temp_path`` is replaced in place when the job
   * finishes, so the path stays valid — but the video must not be shown
   * until then, and the probe fields above are the *source's*: the
   * snapshot carries the corrected ones.
   */
  transcode: { job_id: string; from_codec: string | null; converting: boolean } | null
}

/** What ``video_transcode`` leaves in ``job.result.snapshot``. Phone
 * footage comes back with width/height swapped — the rotation flag is
 * baked into the pixels during the re-encode. */
export interface TranscodeSnapshot {
  encoder: string
  width: number | undefined
  height: number | undefined
  duration: number | undefined
  file_size: number | undefined
  temp_path: string
  video_url: string
  proxy_url: string | null
}

/** Pull the transcode snapshot off a finished job; ``null`` while it
 * runs. Same accept-undefined shape as {@link readCutSnapshot}. */
export function readTranscodeSnapshot(job: Job | undefined): TranscodeSnapshot | null {
  if (!job || job.status !== 'succeeded') return null
  const result = job.result as { snapshot?: TranscodeSnapshot } | null | undefined
  return result?.snapshot ?? null
}

async function readProblem(res: Response, fallback: string): Promise<never> {
  let problem: ProblemDetail | null = null
  const contentType = res.headers.get('content-type') ?? ''
  if (
    contentType.includes('application/json') ||
    contentType.includes('application/problem+json')
  ) {
    try {
      const body = (await res.json()) as ProblemDetail
      if (typeof body.title === 'string') problem = body
    } catch {
      /* ignore */
    }
  }
  const message = problem?.detail ?? problem?.title ?? `${fallback} (HTTP ${String(res.status)})`
  throw new ApiError(message, res.status, problem, null)
}

/**
 * Convert ``temp_path`` returned by the backend into the URL the
 * static ``/output`` mount serves it from. The backend saves uploads
 * under ``<repo>/output/{channel}/.tools/_uploads/{uuid}.mp4`` — we
 * strip everything up to and including ``output/`` and prefix with
 * ``/output`` so the same path works in dev (Vite proxy) and prod
 * (VITE_API_BASE_URL prefix via ``apiUrl``).
 */
function tempPathToUrl(tempPath: string): string {
  // Handle both POSIX and Windows separators just in case.
  const normalized = tempPath.replace(/\\/g, '/')
  const idx = normalized.indexOf('/output/')
  if (idx >= 0) return normalized.slice(idx)
  // Legacy fallback: caller passed an already-relative path.
  return normalized.startsWith('/') ? normalized : `/${normalized}`
}

/**
 * Where an upload is. The request stays open after the last byte goes
 * out — the backend still has to probe the file and remux it for
 * faststart, which is tens of seconds on a multi-GB video — so a bar
 * pinned at 100% would read as frozen. ``processing`` is that window.
 *
 * ``converting`` is the one after it, and only for footage the browser
 * can't decode: a separate job re-encodes to H.264 and reports its own
 * percentage (minutes, not seconds — see the clip-creator doc).
 */
export type UploadPhase = 'uploading' | 'processing' | 'converting'

export interface UploadForCutProgress {
  phase: UploadPhase
  loaded: number
  total: number
  /** ``null`` only while the browser can't compute the total. */
  percent: number | null
}

export interface UploadForCutInput {
  file: File
  /** Progress ticks, throttled to one per whole percent. Safe to pass a
   * ``setState`` directly. */
  onProgress?: (progress: UploadForCutProgress) => void
}

/**
 * Upload a local video file to the backend for later cutting/marking.
 * The response tells us how long the video is so the timeline can
 * scale correctly, and gives us a URL to play the file back.
 */
export function useUploadForCut() {
  return useMutation({
    mutationFn: async ({ file, onProgress }: UploadForCutInput): Promise<UploadedForCut> => {
      const form = new FormData()
      form.append('file', file)

      let lastPercent = -1
      const res = await authUpload('/api/v1/tools/upload-for-cut', form, {
        onProgress: ({ loaded, total, percent }) => {
          // A multi-GB upload fires hundreds of progress events; without
          // this the importer re-renders on every one of them for a bar
          // that only moves in whole percents.
          if (percent !== null && percent === lastPercent) return
          lastPercent = percent ?? -1
          onProgress?.({ phase: 'uploading', loaded, total: total || file.size, percent })
        },
        onUploaded: () => {
          onProgress?.({ phase: 'processing', loaded: file.size, total: file.size, percent: 100 })
        },
      })
      if (!res.ok) await readProblem(res, 'Falha ao enviar vídeo')

      const body = (await res.json()) as { data: Omit<UploadedForCut, 'video_url'> }
      const data = body.data
      return { ...data, video_url: tempPathToUrl(data.temp_path) }
    },
  })
}

// --- Cut & download ---------------------------------------------------------

/** A held frame, in the **source** file's clock. The backend splits the
 * segment around it and loops one extracted frame over silence. */
export interface CutFreeze {
  at: number
  duration: number
}

export interface CutSegment {
  start: number
  end: number
  label: string
  /** Source video for this segment. Omit to use the request-level
   * ``tempPath`` — a project with several videos sends one per segment
   * so a single job can span all of them. */
  temp_path?: string
  /** Playback rate. 1 (or omitted) keeps the source's own speed; the
   * backend applies ``setpts``/``atempo``, so the output is shorter or
   * longer than ``end - start``. */
  speed?: number
  /** Frames held still inside this segment. Each adds its own duration to
   * the output without consuming source footage. */
  freezes?: CutFreeze[]
}

/** Output framing, as fractions of the source frame. Applied to every cut
 * in both modes — it's the shape of the video, not a finishing pass. */
export interface ExportCrop {
  x: number
  y: number
  width: number
  height: number
}

export interface StartCutDownloadInput {
  /** ``temp_path`` returned by ``useUploadForCut`` (for user uploads) or
   * a path served from ``.tools/{jobId}/`` when the source was a
   * YouTube download. */
  tempPath: string
  segments: CutSegment[]
  /** ``download`` (default) yields one MP4 per segment; ``merge`` joins
   * them into a single MP4 following the array order. */
  mode?: 'download' | 'merge'
  /** Merge mode only: the name the joined file downloads as (the backend
   * sanitizes it). Falls back to the source video's stem. */
  mergeLabel?: string
  /** Merge mode only: captions burned onto the joined video. Times are
   * seconds **of the joined file** — the backend composites after the
   * concat, so a caption spanning a join stays whole. */
  captions?: ExportCaption[]
  /** Merge mode only: colour correction ranges, same axis as the captions. */
  grades?: ExportGrade[]
  /** Both modes: the project's framing, or omitted for the whole frame. */
  crop?: ExportCrop
}

/** Wire shape of a caption for the burn-in. Geometry is fractions of the
 * frame, matching ``TextOverlay``; the backend multiplies by the master's
 * real dimensions. */
export interface ExportCaption {
  text: string
  start: number
  end: number
  x: number
  y: number
  size: number
  font_family: string
  color: string
  background: string
  shadow_size: number
  shadow_color: string
  transition_in: ExportTransition
  transition_out: ExportTransition
}

/** Entrance/exit for a caption. The vocabulary is closed to opacity +
 * affine transform so ffmpeg can animate the same single PNG the preview
 * animates with CSS — see ``TextTransition`` in the clip creator's model. */
export interface ExportTransition {
  kind: string
  duration: number
}

/** Colour correction over a stretch of the joined video. The backend
 * reproduces the browser's own filter formulas rather than ffmpeg's ``eq``,
 * so the export matches what the editor previewed. */
export interface ExportGrade {
  start: number
  end: number
  brightness: number
  contrast: number
  saturation: number
  hue: number
}

export interface StartCutDownloadResponse {
  job_id: string
}

/**
 * Shape of the snapshot the backend stashes in ``job.result.snapshot``
 * when ``mode="download"`` succeeds. ``clips[i].index`` mirrors the
 * order of the ``segments`` array we sent — the client uses that to
 * pair each result back to its local clip.
 */
export interface CutClipResult {
  index: number
  label: string
  start: number
  end: number
  duration: number
  download_url: string
  suggested_filename: string
}

/** Single-file result of ``mode="merge"`` — every clip joined in order. */
export interface CutMergedResult {
  clip_count: number
  duration: number
  download_url: string
  suggested_filename: string
}

export interface CutSnapshot {
  status: string
  clips?: CutClipResult[]
  merged?: CutMergedResult
}

/**
 * Kick off a "cut & download" job. Fires ``mode="download"`` at
 * ``/cut-video`` so the clips are left as raw MP4s under
 * ``.tools/{jobId}/``, exposed through the static ``/output`` mount.
 * ``mode="merge"`` instead returns a single joined MP4. Poll the
 * returned job id with {@link useJob}, then read
 * {@link readCutSnapshot} once it succeeds.
 */
export function useStartCutVideo() {
  return useMutation({
    mutationFn: async (input: StartCutDownloadInput): Promise<StartCutDownloadResponse> => {
      const form = new FormData()
      form.append('temp_path', input.tempPath)
      form.append('mode', input.mode ?? 'download')
      form.append('segments', JSON.stringify(input.segments))
      if (input.mergeLabel) form.append('merge_label', input.mergeLabel)
      if (input.captions?.length) form.append('captions', JSON.stringify(input.captions))
      if (input.grades?.length) form.append('grades', JSON.stringify(input.grades))
      if (input.crop) form.append('crop', JSON.stringify(input.crop))
      // Skip metadata generation explicitly — download mode wouldn't
      // use it anyway, but this keeps intent clear and future-proofs
      // against a backend default flip.
      form.append('generate_metadata', 'false')

      const res = await authFetch('/api/v1/tools/cut-video', {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      })
      if (!res.ok) await readProblem(res, 'Falha ao cortar o vídeo')

      const body = (await res.json()) as { data: StartCutDownloadResponse }
      return body.data
    },
  })
}

/**
 * Pull the cut snapshot off a finished job. Returns ``null`` if the
 * job is still running or has no snapshot yet. Same accept-undefined
 * shape as {@link readDownloadSnapshot} in ``youtubeDownload.ts`` so
 * consumers can pass ``useJob().data`` directly.
 */
export function readCutSnapshot(job: Job | undefined): CutSnapshot | null {
  if (!job || job.status !== 'succeeded') return null
  const result = job.result as { snapshot?: CutSnapshot } | null | undefined
  return result?.snapshot ?? null
}

// --- Imports registry (cached upload + youtube-download files) --------------

export interface ImportedItem {
  id: string
  kind: 'upload' | 'youtube'
  filename: string
  temp_path: string
  video_url: string
  proxy_url: string | null
  size_bytes: number
  modified_at: string
}

const IMPORTS_QUERY_KEY = ['tools', 'imports'] as const

/**
 * List cached video files under the channel's ``.tools/`` that the
 * clip creator can re-open. Sorted newest-first by the backend.
 * Refetched on window focus so the list stays fresh after a manual
 * cleanup happens outside the app.
 */
export function useImports() {
  return useQuery({
    queryKey: IMPORTS_QUERY_KEY,
    queryFn: async (): Promise<ImportedItem[]> => {
      const res = await authFetch('/api/v1/tools/imports', { credentials: 'same-origin' })
      if (!res.ok) await readProblem(res, 'Falha ao listar imports')
      const body = (await res.json()) as { data: ImportedItem[] }
      return body.data
    },
    staleTime: 15_000,
  })
}

export function useDeleteImport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await authFetch(`/api/v1/tools/imports/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      })
      if (!res.ok) await readProblem(res, 'Falha ao excluir')
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: IMPORTS_QUERY_KEY })
    },
  })
}

/**
 * Nudge the imports list to refetch — call after a fresh upload or a
 * YouTube-download job succeeds so the new file appears immediately.
 */
export function useInvalidateImports() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: IMPORTS_QUERY_KEY })
  }
}

// --- Legacy clip persistence (pre-ToolProject) ------------------------------
//
// Clips used to be stored per video, keyed by the server-side temp path.
// They now live inside a project (see the clip creator's ``projectStore``),
// and this reader exists only so the one-shot migration can adopt what a
// previous session saved. Nothing writes this format anymore.

const CLIPS_STORAGE_PREFIX = 'prism:clip-creator:clips:v1:'

/** Minimal serializable projection of a clip in the legacy format.
 * ``download`` carried the URL + suggested filename from the last
 * successful cut job for that clip's bounds. */
export interface StoredClip {
  id: number
  start: number
  end: number
  download?: { url: string; filename: string }
}

export function loadStoredClips(tempPath: string): StoredClip[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(CLIPS_STORAGE_PREFIX + tempPath)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (c): c is StoredClip =>
          typeof c === 'object' &&
          c !== null &&
          typeof (c as StoredClip).id === 'number' &&
          typeof (c as StoredClip).start === 'number' &&
          typeof (c as StoredClip).end === 'number',
      )
      .map((c) => {
        const dl = c.download
        const validDownload =
          dl &&
          typeof dl === 'object' &&
          typeof dl.url === 'string' &&
          typeof dl.filename === 'string'
        return validDownload
          ? { id: c.id, start: c.start, end: c.end, download: { url: dl.url, filename: dl.filename } }
          : { id: c.id, start: c.start, end: c.end }
      })
  } catch {
    // Corrupted JSON, quota exceeded, private mode — treat as no data.
    return []
  }
}
